/**
 * Fait : un champ de formulaire dont le libellé, l'aide et le message d'erreur
 *   sont réellement rattachés à la saisie — par `id`, `aria-describedby` et
 *   `aria-invalid` — de sorte qu'un lecteur d'écran les annonce au bon moment.
 *
 * Ne fait pas : il ne valide rien. La validation appartient aux schémas de
 *   `@aymoai/shared`, partagés avec le serveur. Un champ qui validerait
 *   lui-même écrirait la règle une seconde fois, et les deux divergeraient.
 */

import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';

export interface ChampProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  /** Libellé visible. Jamais un simple texte d'invite : celui-ci disparaît. */
  readonly libelle: string;

  /** Aide facultative, annoncée après le libellé. */
  readonly aide?: ReactNode;

  /** Message d'erreur. Sa présence marque le champ comme invalide. */
  readonly erreur?: string;
}

/**
 * Un champ accessible.
 *
 * L'erreur porte `role="alert"` : elle interrompt la lecture en cours, ce qui
 * est justifié — sans cela, un utilisateur non voyant valide un formulaire et
 * ne sait pas pourquoi rien ne se passe.
 *
 * L'ŒIL EST POSÉ ICI, ET NON DANS UN COMPOSANT QU'ON PENSE À EMPLOYER. Décidé
 * par Charles le 2026-08-24 : « tous les champs mot de passe doivent avoir un
 * œil à droite ». Le mettre dans le gabarit rend l'oubli IMPOSSIBLE — tout
 * champ de saisie du produit passe par ici, donc tout champ de mot de passe
 * porte son œil, sans que personne ait à y penser.
 *
 * CE QUE COÛTAIT L'ANCIENNE FORME, mesuré le jour même : un composant
 * `ChampMotDePasse` portait une case « Afficher le mot de passe », et il
 * n'était employé que sur DEUX écrans sur quatre. La connexion et la
 * suppression de compte avaient un `password` nu — impossible d'y relire ce
 * qu'on tape. Pour quelqu'un qui voit mal, c'est la porte d'entrée du produit
 * qui se referme, et c'est exactement là que ça manquait.
 */
export function Champ({ libelle, aide, erreur, ...proprietesDeLaSaisie }: ChampProps) {
  const identifiantDuChamp = useId();
  const identifiantDeLAide = `${identifiantDuChamp}-aide`;
  const identifiantDeLErreur = `${identifiantDuChamp}-erreur`;

  const [devoile, setDevoile] = useState(false);
  const estUnMotDePasse = proprietesDeLaSaisie.type === 'password';

  const decritPar = [
    aide !== undefined ? identifiantDeLAide : undefined,
    erreur !== undefined ? identifiantDeLErreur : undefined,
  ]
    .filter((valeur) => valeur !== undefined)
    .join(' ');

  return (
    <p className="champ">
      <label htmlFor={identifiantDuChamp}>{libelle}</label>

      {aide !== undefined && (
        <span id={identifiantDeLAide} className="texte-attenue">
          {aide}
        </span>
      )}

      <span className={estUnMotDePasse ? 'champ-avec-oeil' : undefined}>
        <input
          id={identifiantDuChamp}
          aria-invalid={erreur !== undefined}
          {...(decritPar !== '' ? { 'aria-describedby': decritPar } : {})}
          {...proprietesDeLaSaisie}
          {...(estUnMotDePasse ? { type: devoile ? 'text' : 'password' } : {})}
        />

        {estUnMotDePasse && (
          /*
           * UN BOUTON, ET NON UNE CASE À COCHER. Il vit DANS le champ, à
           * droite, là où l'œil se cherche. `aria-pressed` dit son état à un
           * lecteur d'écran, et son libellé change avec lui : « Afficher » /
           * « Masquer ». `tabIndex={-1}` serait une faute — on doit pouvoir
           * l'atteindre au clavier seul, comme tout le reste du produit.
           */
          <button
            type="button"
            className="oeil-du-mot-de-passe"
            data-role="oeil-du-mot-de-passe"
            aria-pressed={devoile}
            aria-label={devoile ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            title={devoile ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            onClick={() => setDevoile((avant) => !avant)}
          >
            {/*
             * UN DESSIN, PAS UN ÉMOJI. Un émoji change de forme et de taille
             * selon la police du système, et se colore tout seul — on ne peut
             * ni garantir son contraste ni sa lisibilité à fort zoom. Ce tracé
             * suit `currentColor` et grandit avec le texte.
             *
             * Décoratif — `aria-hidden` — parce que le bouton porte déjà son
             * nom. Barré quand le mot de passe est visible : c'est le signe
             * attendu, et il dit ce qu'un clic ferait.
             */}
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
              <path
                d="M1.5 12S5.5 5 12 5s10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              />
              <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="2" />
              {devoile && (
                <path d="M4 20 20 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              )}
            </svg>
          </button>
        )}
      </span>

      {erreur !== undefined && (
        <span id={identifiantDeLErreur} role="alert" className="message-erreur">
          {erreur}
        </span>
      )}
    </p>
  );
}
